import { useEffect, useState } from "react";
import { Plus, Trash, X, Copy, UsersThree, Crown, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Th, Td } from "./TrainerSchedule";
import { useAuth } from "../../context/AuthContext";

export default function TrainerTeam() {
  const { user } = useAuth();
  const [trainers, setTrainers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([api.get("/trainers"), api.get("/clients")]);
      setTrainers(t.data);
      setClients(c.data);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  // Route guard: non-owner trainers shouldn't see this page
  if (user && !user.is_owner) {
    return <Navigate to="/trainer" replace />;
  }

  const remove = async (id, name) => {
    if (!window.confirm(`Remove trainer "${name}"? This is only allowed if they have no clients assigned.`)) return;
    try {
      const { data } = await api.delete(`/trainers/${id}`);
      toast.success(data.status === "deactivated" ? "Trainer deactivated (kept for history)" : "Trainer removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/trainers/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Deactivated" : "Activated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reassign = async (clientId, trainerId) => {
    try {
      const { data } = await api.patch(`/clients/${clientId}/trainer`, { trainer_id: trainerId });
      toast.success(`Re-assigned to ${data.trainer_name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Build a quick map id -> name for display
  const trainerMap = Object.fromEntries(trainers.map((t) => [t.id, t.name]));
  const clientsByTrainer = trainers.map((t) => ({
    trainer: t,
    list: clients.filter((c) => c.trainer_id === t.id),
  }));
  const unassigned = clients.filter((c) => !c.trainer_id || !trainerMap[c.trainer_id]);
  const activeTrainers = trainers.filter((t) => t.active !== false);

  return (
    <div className="space-y-8" data-testid="trainer-team">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Team ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Coaches.</h1>
          <p className="text-zinc-500 mt-2">Add coaches to your studio. Each coach sees only their own clients — you see everyone.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="open-trainer-form">
          <Plus size={16} weight="bold" /> Add trainer
        </button>
      </div>

      {/* Trainers table */}
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#0F0F0F]">
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Role</Th>
              <Th>Commission</Th>
              <Th>Access code</Th>
              <Th>Clients</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {trainers.map((t) => {
              const clientCount = clients.filter((c) => c.trainer_id === t.id).length;
              const isMe = t.id === user?.id;
              return (
                <tr key={t.id} className="border-t border-white/5" data-testid={`trainer-row-${t.id}`}>
                  <Td>
                    <div className="flex items-center gap-2">
                      {t.is_owner && <Crown size={14} weight="fill" className="text-[#FFC700]" />}
                      <span className="font-semibold">{t.name}</span>
                      {isMe && <span className="tracker text-[9px] text-brand-light">(you)</span>}
                    </div>
                  </Td>
                  <Td><span className="font-mono text-xs">{t.email}</span></Td>
                  <Td><span className="font-mono text-xs">{t.phone || "—"}</span></Td>
                  <Td><span className="tracker">{t.is_owner ? "Owner" : "Trainer"}</span></Td>
                  <Td>
                    <CommissionEditor trainer={t} onSaved={load} />
                  </Td>
                  <Td>
                    <AccessCodeCell trainer={t} canEdit={user?.is_owner || t.id === user?.id} onRotated={load} />
                  </Td>
                  <Td><span className="font-mono">{clientCount}</span></Td>
                  <Td>
                    {t.active === false ? (
                      <span className="tracker text-zinc-500">Inactive</span>
                    ) : (
                      <span className="tracker text-emerald-400">Active</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    {!t.is_owner && (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => toggleActive(t)} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`toggle-${t.id}`}>
                          {t.active === false ? "Activate" : "Deactivate"}
                        </button>
                        <button onClick={() => remove(t.id, t.name)} className="btn-ghost !py-1.5 !px-3 !text-[10px] text-red-400" data-testid={`del-${t.id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    )}
                  </Td>
                </tr>
              );
            })}
            {trainers.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-10 text-center text-zinc-600">No trainers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Re-assignment view */}
      <div>
        <div className="tracker text-brand mb-3">[ Client Schedule by Coach ]</div>
        <div className="grid lg:grid-cols-2 gap-4 min-w-0">
          {clientsByTrainer.map(({ trainer, list }) => (
            <div key={trainer.id} className="card-flat p-5 min-w-0" data-testid={`coach-bucket-${trainer.id}`}>
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <UsersThree size={18} weight="duotone" className="text-brand shrink-0" />
                  <div className="font-semibold truncate">{trainer.name}</div>
                  {trainer.is_owner && <Crown size={12} weight="fill" className="text-[#FFC700] shrink-0" />}
                </div>
                <div className="tracker text-zinc-500 shrink-0">{list.length} client{list.length === 1 ? "" : "s"}</div>
              </div>
              {list.length === 0 ? (
                <div className="text-zinc-600 text-xs italic">No clients yet.</div>
              ) : (
                <ul className="space-y-2">
                  {list.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`bucket-client-${c.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{c.name}</div>
                        <div className="font-mono text-[10px] text-zinc-500 truncate">{c.email}</div>
                      </div>
                      <select
                        value={trainer.id}
                        onChange={(e) => e.target.value !== trainer.id && reassign(c.id, e.target.value)}
                        className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-xs tracker shrink-0"
                        data-testid={`reassign-${c.id}`}
                      >
                        {activeTrainers.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}{t.is_owner ? " (Owner)" : ""}</option>
                        ))}
                      </select>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {unassigned.length > 0 && (
            <div className="card-flat p-5 border-orange-500/40" data-testid="unassigned-bucket">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-orange-400">Unassigned</div>
                <div className="tracker text-zinc-500">{unassigned.length}</div>
              </div>
              <ul className="space-y-2">
                {unassigned.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="font-mono text-[10px] text-zinc-500 truncate">{c.email}</div>
                    </div>
                    <select
                      onChange={(e) => e.target.value && reassign(c.id, e.target.value)}
                      defaultValue=""
                      className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-xs tracker shrink-0"
                      data-testid={`assign-${c.id}`}
                    >
                      <option value="" disabled>Assign to…</option>
                      {activeTrainers.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}{t.is_owner ? " (Owner)" : ""}</option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {showForm && <TrainerForm onClose={() => setShowForm(false)} onCreated={(out) => { setCreatedInfo(out); load(); }} />}
      {createdInfo && <PasswordRevealModal info={createdInfo} onClose={() => setCreatedInfo(null)} />}
    </div>
  );
}

function TrainerForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", commission_percent: 70 });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        commission_percent: Number(form.commission_percent),
      };
      if (form.password) payload.password = form.password;
      const { data } = await api.post("/trainers", payload);
      toast.success("Trainer added");
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="trainer-form-modal">
      <form onSubmit={submit} className="card-flat p-6 sm:p-8 w-full max-w-md my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-3xl">Add trainer</h2>
          <button type="button" onClick={onClose} className="btn-ghost !py-1.5 !px-2"><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-4">
          <FormField label="Full name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border" data-testid="trainer-name" required />
          </FormField>
          <FormField label="Email">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border" data-testid="trainer-email" required />
          </FormField>
          <FormField label="Phone (optional)">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 border" placeholder="+1 555 123 4567" data-testid="trainer-phone" />
          </FormField>
          <FormField label="Commission % (trainer's cut of paid invoices)">
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={form.commission_percent}
                onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                className="w-32 px-4 py-3 border"
                data-testid="trainer-commission"
              />
              <span className="font-mono text-zinc-500">% — studio keeps {Math.max(0, 100 - Number(form.commission_percent || 0))}%</span>
            </div>
          </FormField>
          <FormField label="Password (leave blank to auto-generate)">
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 border" placeholder="Auto-generated if blank" data-testid="trainer-password" />
          </FormField>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="trainer-submit">
            {submitting ? "Adding…" : "Add trainer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MyAccessCodeBanner({ code, userId, onRotated }) {
  const copyLink = () => {
    const url = `${window.location.origin}/register?code=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Sign-up link copied — share it with your clients");
  };
  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Access code copied");
  };
  const rotate = async () => {
    if (!window.confirm("Rotate your access code? Old code stops working immediately. Existing clients keep their accounts.")) return;
    try {
      const { data } = await api.post(`/trainers/${userId}/rotate-access-code`);
      toast.success(`New code: ${data.access_code}`);
      onRotated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <div className="card-flat p-6 border border-brand/40 bg-brand/5" data-testid="my-access-code-banner">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand-light mb-2">[ Your access code ]</div>
          <div className="font-display text-3xl tracking-widest font-mono" data-testid="my-code">{code}</div>
          <p className="text-zinc-400 text-sm mt-2">Share this code with new clients — they'll enter it when signing up to join you.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={copyCode} className="btn-ghost" data-testid="copy-my-code"><Copy size={14} weight="bold" /> Copy code</button>
          <button onClick={copyLink} className="btn-primary" data-testid="copy-my-link"><Copy size={14} weight="bold" /> Copy sign-up link</button>
          <button onClick={rotate} className="btn-ghost" data-testid="rotate-my-code" title="Rotate"><ArrowsClockwise size={14} weight="bold" /></button>
        </div>
      </div>
    </div>
  );
}

function AccessCodeCell({ trainer, canEdit, onRotated }) {
  const [busy, setBusy] = useState(false);
  const code = trainer.access_code || "—";
  const copy = () => {
    if (!trainer.access_code) return;
    navigator.clipboard.writeText(trainer.access_code);
    toast.success("Access code copied");
  };
  const rotate = async () => {
    if (!window.confirm("Rotate the access code? Anyone with the old code can no longer register under this coach. Existing clients are unaffected.")) return;
    setBusy(true);
    try {
      await api.post(`/trainers/${trainer.id}/rotate-access-code`);
      toast.success("Access code rotated");
      onRotated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm tracking-widest" data-testid={`code-${trainer.id}`}>{code}</span>
      {trainer.access_code && (
        <button onClick={copy} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`copy-code-${trainer.id}`} title="Copy">
          <Copy size={11} weight="bold" />
        </button>
      )}
      {canEdit && trainer.access_code && (
        <button onClick={rotate} disabled={busy} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`rotate-code-${trainer.id}`} title="Rotate">
          <ArrowsClockwise size={11} weight="bold" />
        </button>
      )}
    </div>
  );
}

function CommissionEditor({ trainer, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(trainer.commission_percent ?? (trainer.is_owner ? 100 : 70));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = Math.max(0, Math.min(100, Number(val)));
    setSaving(true);
    try {
      await api.patch(`/trainers/${trainer.id}`, { commission_percent: n });
      toast.success("Commission updated");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => !trainer.is_owner && setEditing(true)}
        disabled={trainer.is_owner}
        className={`font-mono text-sm ${trainer.is_owner ? "text-zinc-500 cursor-default" : "text-brand-light hover:underline cursor-pointer"}`}
        data-testid={`commission-display-${trainer.id}`}
        title={trainer.is_owner ? "Owner keeps 100% (no split)" : "Click to edit"}
      >
        {(trainer.commission_percent ?? (trainer.is_owner ? 100 : 70)).toFixed(0)}%
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min="0"
        max="100"
        step="1"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        autoFocus
        className="w-16 px-2 py-1 border text-sm"
        data-testid={`commission-input-${trainer.id}`}
      />
      <button onClick={save} disabled={saving} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`commission-save-${trainer.id}`}>
        {saving ? "…" : "Save"}
      </button>
      <button onClick={() => setEditing(false)} className="btn-ghost !py-1 !px-2 !text-[10px]">Cancel</button>
    </div>
  );
}

function PasswordRevealModal({ info, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(info.temp_password);
    toast.success("Password copied");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="password-reveal">
      <div className="card-flat p-6 sm:p-8 w-full max-w-md my-6">
        <CheckCircle size={36} weight="duotone" className="text-emerald-400 mb-3" />
        <h2 className="font-display text-3xl mb-2">Trainer added</h2>
        <p className="text-zinc-400 mb-6 text-sm">Share these credentials with <span className="font-semibold text-white">{info.name}</span>. This is the only time the password is shown.</p>
        <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="tracker text-zinc-500">Email</span>
            <span className="font-mono text-sm">{info.email}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="tracker text-zinc-500">Password</span>
            <span className="font-mono text-sm" data-testid="reveal-password">{info.temp_password}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={copy} className="btn-ghost flex-1" data-testid="copy-password">
            <Copy size={14} weight="bold" /> Copy password
          </button>
          <button onClick={onClose} className="btn-primary flex-1" data-testid="reveal-close">Done</button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="tracker text-zinc-500 mb-2">{label}</div>
      {children}
    </label>
  );
}
